import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "./PayrollV2Detail.css";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "https://api.comnieuthienly.vn/api"
).replace(/\/$/, "");

const STATUS_LABELS = {
  sent: "Chờ xác nhận",
  confirmed: "Đã xác nhận",
  paid: "Đã trả lương",
};

const TIME_ROWS = [
  ["Ngày công thực tế", "worked_days", "days"],
  ["Tự chấm công", "employee_self_seconds"],
  ["Chấm công hộ", "proxy_seconds"],
  ["Thêm giờ", "added_seconds"],
  ["Nghỉ bù", "compensatory_seconds"],
  ["Quên chấm công được duyệt", "missed_attendance_seconds"],
  ["Nghỉ phép được duyệt", "leave_seconds"],
  ["Công tác được duyệt", "business_trip_seconds"],
  ["Dữ liệu công khác", "other_seconds"],
  ["Tổng giờ công thực tế", "actual_seconds"],
  ["Giờ công thường", "regular_seconds"],
  ["Giờ trong định mức", "standard_seconds"],
  ["Giờ vượt định mức", "excess_seconds"],
  ["Giờ tăng ca", "overtime_seconds"],
  ["Giờ làm ngày lễ", "holiday_seconds"],
  ["Tổng thời gian ghi nhận", "total_seconds"],
];

const INCOME_ROWS = [
  ["P1 - Lương vị trí", "position_salary"],
  ["P2 - Lương năng suất", "support_productive"],
  ["KPI/P3 - Lương kết quả", "support_result"],
  ["Lương chi ngoài", "outside_salary"],
  ["Tiền tăng ca", "overtime"],
  ["Tiền ngày lễ", "holiday"],
  ["Tổng lương thường", "regular_total"],
  ["Tổng tăng ca và ngày lễ", "overtime_total"],
  ["Tổng thu nhập theo công", "total"],
];

const SOURCE_ROWS = [
  ["Thu nhập từ tự chấm", "employee_self"],
  ["Thu nhập từ chấm hộ", "proxy"],
  ["Thu nhập từ thêm giờ", "added"],
  ["Thu nhập từ nghỉ bù", "compensatory"],
  ["Thu nhập từ quên chấm công", "missed_attendance"],
  ["Thu nhập từ nghỉ phép", "leave"],
  ["Thu nhập từ công tác", "business_trip"],
];

const ALLOCATION_ROWS = [
  ["Giờ công thực tế", "worked_time", "time"],
  ["Giờ công thường", "regular_work_time", "time"],
  ["Giờ trong định mức", "standard_work_time", "time"],
  ["Giờ vượt định mức", "excess_work_time", "time"],
  ["Ngày công", "worked_days", "days"],
  ["KPI/P3", "kpi_percent", "percent"],
  ["Thu nhập trực tiếp", "direct_income"],
  ["Thu nhập chung phân bổ", "allocated_global_income"],
  ["BHXH phân bổ", "allocated_bhxh"],
  ["BHYT phân bổ", "allocated_bhyt"],
  ["BHTN phân bổ", "allocated_bhtn"],
  ["Công đoàn phân bổ", "allocated_union_fee"],
  ["Thuế phân bổ", "allocated_tax"],
  ["Khoản cộng phân bổ", "allocated_another_plus"],
  ["Khoản trừ phân bổ", "allocated_another_minus"],
  ["Cộng sau thuế phân bổ", "allocated_another_plus_after_tax"],
  ["Trừ sau thuế phân bổ", "allocated_another_minus_after_tax"],
  ["Thực nhận phân bổ", "allocated_net_income"],
];

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatMoney = (value) =>
  `${numberValue(value).toLocaleString("vi-VN", { maximumFractionDigits: 0 })} ₫`;

const formatTime = (value) => {
  const totalSeconds = Math.max(0, Math.round(numberValue(value)));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (!hours && !minutes) return "0 giờ";
  return `${hours ? `${hours} giờ` : ""}${hours && minutes ? " " : ""}${
    minutes ? `${minutes} phút` : ""
  }`;
};

const formatValue = (value, kind) => {
  if (kind === "time") return formatTime(value);
  if (kind === "days") return `${numberValue(value).toLocaleString("vi-VN")} ngày`;
  if (kind === "percent") return `${numberValue(value).toLocaleString("vi-VN")} %`;
  return formatMoney(value);
};

const componentAmount = (component) =>
  component && typeof component === "object" ? component.amount : component;

function DetailRows({ rows }) {
  return (
    <dl className="payroll-v2-rows">
      {rows.map(([label, value, kind = "money"], index) => (
        <div className="payroll-v2-row" key={`${label}-${index}`}>
          <dt>{label}</dt>
          <dd>{formatValue(value, kind)}</dd>
        </div>
      ))}
    </dl>
  );
}

function Section({ title, description, children, className = "" }) {
  return (
    <section className={`payroll-v2-card ${className}`}>
      <div className="payroll-v2-card-heading">
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

function PayrollV2Detail() {
  const { token } = useParams();
  const [payroll, setPayroll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(
          `${API_BASE_URL}/personnel/payroll-v2/public/${encodeURIComponent(token)}`,
          { headers: { Accept: "application/json" } }
        );
        const body = await response.json().catch(() => ({}));
        if (!response.ok || !body.success || !body.data) {
          throw new Error(body.msg || "Bảng lương không tồn tại hoặc liên kết không hợp lệ.");
        }
        if (active) setPayroll(body.data);
      } catch (loadError) {
        if (active) setError(loadError.message || "Không thể tải bảng lương.");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [token]);

  const confirmPayroll = async () => {
    setConfirming(true);
    setNotice("");
    try {
      const response = await fetch(
        `${API_BASE_URL}/personnel/payroll-v2/public/${encodeURIComponent(token)}/confirm`,
        {
          method: "POST",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
        }
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body.success || !body.data) {
        throw new Error(body.msg || "Không thể xác nhận bảng lương.");
      }
      setPayroll(body.data);
      setNotice("Bảng lương đã được xác nhận thành công.");
    } catch (confirmError) {
      setNotice(confirmError.message || "Không thể xác nhận bảng lương.");
    } finally {
      setConfirming(false);
    }
  };

  const version = payroll?.current_version;
  const detail = version?.detail;
  const allScope = useMemo(
    () => detail?.scopes?.find((scope) => scope.scope === "all") || detail?.scopes?.[0],
    [detail]
  );

  if (loading) {
    return (
      <main className="payroll-v2-state" aria-live="polite">
        <div className="payroll-v2-spinner" />
        <p>Đang tải bảng lương...</p>
      </main>
    );
  }

  if (error || !payroll) {
    return (
      <main className="payroll-v2-state payroll-v2-state-error" role="alert">
        <span className="payroll-v2-state-icon">!</span>
        <h1>Không thể mở bảng lương</h1>
        <p>{error || "Bảng lương không tồn tại hoặc liên kết không hợp lệ."}</p>
      </main>
    );
  }

  const time = allScope?.time || {};
  const income = allScope?.income || {};
  const allowances = allScope?.allowances || {};
  const general = detail?.general || {};
  const adjustments = detail?.adjustments || {};
  const deductions = detail?.deductions || {};
  const postTax = detail?.post_tax_adjustments || {};
  const summary = detail?.summary || {};
  const allocations = version?.allocations || [];

  return (
    <main className="payroll-v2-page">
      <header className="payroll-v2-hero">
        <div>
          <p className="payroll-v2-eyebrow">BẢNG ĐỐI CHIẾU LƯƠNG</p>
          <h1>{payroll.staff?.name || "Nhân viên"}</h1>
          <p className="payroll-v2-period">Kỳ lương tháng {payroll.month}/{payroll.year}</p>
        </div>
        <span className={`payroll-v2-status payroll-v2-status-${payroll.status}`}>
          {STATUS_LABELS[payroll.status] || payroll.status}
        </span>
      </header>

      <div className="payroll-v2-content">
        <Section title="Thông tin phiếu lương">
          <div className="payroll-v2-info-grid">
            <div><span>Mã nhân viên</span><strong>{payroll.staff?.staff_code || "—"}</strong></div>
            <div><span>Chức vụ</span><strong>{payroll.staff?.position || "—"}</strong></div>
            <div><span>Phòng ban</span><strong>{payroll.staff?.department || "—"}</strong></div>
            <div><span>Ngày vào làm</span><strong>{payroll.staff?.start_join_time || "—"}</strong></div>
            <div><span>Phiên bản</span><strong>{version ? `Lần ${version.version_no}` : "—"}</strong></div>
            <div><span>Chi nhánh ghi nhận</span><strong>{allocations.map((item) => item.area_name).filter(Boolean).join(", ") || "—"}</strong></div>
          </div>
        </Section>

        {!version ? (
          <div className="payroll-v2-alert payroll-v2-alert-warning">
            Phiếu lương chưa có phiên bản dữ liệu để hiển thị.
          </div>
        ) : null}

        {version && !detail?.available ? (
          <>
            <div className="payroll-v2-alert payroll-v2-alert-warning" role="status">
              Phiếu lương cũ chưa lưu snapshot chi tiết. Các tổng số đã lưu vẫn được hiển thị bên dưới; hệ thống không tự tính lại dữ liệu lịch sử.
            </div>
            <Section title="Tổng số đã lưu">
              <DetailRows rows={[
                ["Ngày công", version.worked_days, "days"],
                ["Giờ công chuẩn", version.time_default, "time"],
                ["Giờ công thực tế", version.worked_time, "time"],
                ["P1 - Lương vị trí", version.position_salary],
                ["P2 - Lương năng suất", version.support_productive],
                ["KPI/P3 - Lương kết quả", version.support_result],
                ["Phụ cấp", version.allowance_total],
                ["Thưởng", version.bonus_total],
                ["Lương chi ngoài", version.outside_salary],
                ["Tổng thu nhập", version.gross_income],
                ["Thực nhận cuối", version.net_income],
              ]} />
            </Section>
          </>
        ) : null}

        {detail?.available ? (
          <>
            <div className="payroll-v2-two-column">
              <Section title="Thời gian làm việc">
                <DetailRows rows={[
                  ["Ngày công chuẩn", general.standard_days, "days"],
                  ["Giờ công chuẩn", general.standard_seconds, "time"],
                  ...TIME_ROWS.map(([label, key, kind]) => [label, time[key], kind || "time"]),
                ]} />
                <div className="payroll-v2-subsection">
                  <h3>Chi tiết ca</h3>
                  {(time.shifts || []).length ? time.shifts.map((shift, index) => (
                    <div className="payroll-v2-shift" key={`${shift.title}-${index}`}>
                      <div><strong>{shift.title || `Ca ${index + 1}`}</strong><span>{shift.time_range || "Không có khung giờ"}</span></div>
                      <div><strong>{formatTime(shift.seconds)}</strong><span>{formatMoney(shift.amount)}</span></div>
                    </div>
                  )) : <p className="payroll-v2-empty">Không phát sinh ca làm việc.</p>}
                </div>
                <div className="payroll-v2-subsection">
                  <h3>Chi tiết công tác</h3>
                  {(time.business_trip_destinations || []).length ? time.business_trip_destinations.map((trip, index) => (
                    <div className="payroll-v2-shift" key={`${trip.area_name}-${index}`}>
                      <strong>{trip.area_name || "Chưa xác định chi nhánh"}</strong>
                      <strong>{formatTime(trip.seconds)}</strong>
                    </div>
                  )) : <p className="payroll-v2-empty">Không phát sinh công tác.</p>}
                </div>
              </Section>

              <Section title="Thu nhập theo công">
                <DetailRows rows={INCOME_ROWS.map(([label, key]) => [label, componentAmount(income[key])])} />
                <div className="payroll-v2-subsection">
                  <h3>Nguồn chấm công</h3>
                  <DetailRows rows={SOURCE_ROWS.map(([label, key]) => [label, componentAmount(income.sources?.[key])])} />
                </div>
              </Section>
            </div>

            <div className="payroll-v2-two-column">
              <Section title="Phụ cấp">
                <DetailRows rows={[
                  ["Phụ cấp ăn", componentAmount(allowances.food)],
                  ["Phụ cấp đi lại", componentAmount(allowances.transport)],
                  ["Phụ cấp điện thoại", componentAmount(allowances.phone)],
                  ...((allowances.custom_items || []).map((item) => [item.title || "Phụ cấp động", item.amount])),
                  ["Tổng phụ cấp", componentAmount(allowances.total)],
                ]} />
              </Section>

              <Section title="Khoản cộng và thưởng">
                <DetailRows rows={[
                  ...((adjustments.items || []).map((item) => [item.title || "Khoản cộng", item.amount])),
                  ["Khoản cộng khác", adjustments.another_plus],
                  ["Tổng khoản cộng", adjustments.total],
                ]} />
              </Section>
            </div>

            <div className="payroll-v2-two-column">
              <Section title="Khoản trừ, bảo hiểm và thuế">
                <DetailRows rows={[
                  ...((deductions.items || []).map((item) => [item.title || "Khoản trừ", item.amount])),
                  ["Khoản trừ khác", deductions.another_minus],
                  ["BHXH", deductions.bhxh],
                  ["BHYT", deductions.bhyt],
                  ["BHTN", deductions.bhtn],
                  ["Tổng bảo hiểm", deductions.insurance_total],
                  ["Công đoàn", deductions.union_fee],
                  ["Giảm trừ bản thân", version.dependent_money],
                  ["Giảm trừ người phụ thuộc", version.dependent_another],
                  ["Thu nhập chịu thuế", deductions.taxable_income],
                  ["Thuế TNCN", deductions.tax],
                  ["Tổng khấu trừ", deductions.total],
                ]} />
              </Section>

              <Section title="Điều chỉnh sau thuế">
                <DetailRows rows={[
                  ["Thực nhận trước điều chỉnh", postTax.net_income_before_post_tax_adjustments],
                  ["Cộng sau thuế", postTax.another_plus_after_tax],
                  ["Trừ sau thuế", postTax.another_minus_after_tax],
                  ["Thực nhận sau điều chỉnh", postTax.net_income_after_post_tax_adjustments],
                ]} />
              </Section>
            </div>

            <Section
              title="Phân bổ theo chi nhánh"
              description="Số liệu dưới đây là số liệu phân bổ phục vụ đối soát, không phải các khoản thanh toán riêng biệt."
            >
              <div className="payroll-v2-allocation-grid">
                {allocations.length ? allocations.map((allocation, index) => (
                  <article className="payroll-v2-allocation" key={`${allocation.area_name}-${index}`}>
                    <h3>{allocation.area_name || `Chi nhánh ${index + 1}`}</h3>
                    <DetailRows rows={ALLOCATION_ROWS.map(([label, key, kind]) => [label, allocation[key], kind])} />
                  </article>
                )) : <p className="payroll-v2-empty">Không có dữ liệu phân bổ chi nhánh.</p>}
              </div>
            </Section>

            <section className="payroll-v2-total-card">
              <div>
                <span>Tổng thu nhập</span>
                <strong>{formatMoney(summary.gross_income ?? version.gross_income)}</strong>
                <small>Lương chi ngoài: {formatMoney(summary.outside_salary ?? version.outside_salary)}</small>
              </div>
              <div className="payroll-v2-net-income">
                <span>Thực nhận cuối</span>
                <strong>{formatMoney(summary.net_income ?? version.net_income)}</strong>
              </div>
            </section>
          </>
        ) : null}

        <div className="payroll-v2-confirmation">
          {notice ? <p className="payroll-v2-notice" role="status">{notice}</p> : null}
          {payroll.can_confirm && payroll.status === "sent" ? (
            <button type="button" onClick={confirmPayroll} disabled={confirming}>
              {confirming ? "Đang xác nhận..." : "Xác nhận bảng lương"}
            </button>
          ) : (
            <p className="payroll-v2-confirmed-message">
              {payroll.status === "paid"
                ? "Phiếu lương đã được trả. Chi tiết vẫn được giữ để bạn đối chiếu."
                : "Phiếu lương đã được xác nhận. Chi tiết vẫn được giữ để bạn đối chiếu."}
            </p>
          )}
        </div>
      </div>
    </main>
  );
}

export default PayrollV2Detail;
export { formatMoney, formatTime };
